import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit'
import { portfoliosAPI } from '@/features/portfolios/api/api'
import type { Portfolio, SharedPortfolio, PortfolioRole } from '@/features/assets/api/types'
import type { RootState } from './store'

// Chỉ lưu activeId vào localStorage cho trường hợp shared portfolio
// (user không thể set is_default cho shared, nên cần cách khác để nhớ).
// Với own portfolio: F5 luôn lấy theo is_default của BE.
const SHARED_STORAGE_KEY = 'assets_shared_portfolio_id'

function loadSharedActiveId(): number | null {
  const stored = localStorage.getItem(SHARED_STORAGE_KEY)
  if (!stored) return null
  const parsed = Number(stored)
  return Number.isFinite(parsed) ? parsed : null
}

interface PortfolioState {
  own: Portfolio[]
  shared: SharedPortfolio[]
  activeId: number | null
  status: 'idle' | 'loading' | 'succeeded' | 'failed'
  lastFetchedAt: number | null
}

const initialState: PortfolioState = {
  own: [],
  shared: [],
  activeId: loadSharedActiveId(),
  status: 'idle',
  lastFetchedAt: null,
}

export const fetchPortfolios = createAsyncThunk(
  'portfolio/fetch',
  async () => {
    const res = await portfoliosAPI.list()
    if (res.success) return res.data
    throw new Error(res.message)
  },
  {
    condition: (_, { getState }) => {
      const { portfolio } = getState() as RootState
      return portfolio.status !== 'loading'
    },
  }
)

/** Tìm role cho activeId */
function resolveRole(activeId: number | null, own: Portfolio[], shared: SharedPortfolio[]): PortfolioRole {
  if (activeId === null) return 'owner'
  if (own.some((p) => p.id === activeId)) return 'owner'
  const sh = shared.find((s) => s.portfolio_id === activeId)
  if (sh) return sh.role
  return 'owner'
}

const portfolioSlice = createSlice({
  name: 'portfolio',
  initialState,
  reducers: {
    setActivePortfolio: (state, action: PayloadAction<number | null>) => {
      state.activeId = action.payload
    },
    invalidatePortfolios: (state) => {
      state.lastFetchedAt = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPortfolios.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(fetchPortfolios.fulfilled, (state, action) => {
        state.own = action.payload.own
        state.shared = action.payload.shared
        state.status = 'succeeded'
        state.lastFetchedAt = Date.now()

        // Nếu activeId từ localStorage là shared portfolio còn hợp lệ → giữ nguyên.
        // Ngược lại luôn dùng is_default của BE.
        const sharedValid = state.activeId !== null
          && action.payload.shared.some((s) => s.portfolio_id === state.activeId)
        if (!sharedValid) {
          const def = action.payload.own.find((p) => p.is_default === 1)
          state.activeId = def?.id ?? action.payload.own[0]?.id ?? action.payload.shared[0]?.portfolio_id ?? null
          localStorage.removeItem(SHARED_STORAGE_KEY)
        }
      })
      .addCase(fetchPortfolios.rejected, (state) => {
        state.status = 'failed'
      })
  },
})

export const { setActivePortfolio, invalidatePortfolios } = portfolioSlice.actions

export const selectActiveRole = (state: RootState): PortfolioRole =>
  resolveRole(state.portfolio.activeId, state.portfolio.own, state.portfolio.shared)

export default portfolioSlice.reducer
